import { View, SxProp, Image, useDripsyTheme, styled } from 'dripsy';
import React, { useState, useContext, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { useNavigation } from '@react-navigation/native';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { H3, H5, H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { Row } from '@/design-system/components/layout';
import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { DateRangePicker } from '@/design-system/components/dateRangePicker';
import { UniInput } from '@/design-system/components/inputs';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import {
  TransactionStatusFilters,
  CardStatusFilters,
  CardStatusSelectFilters,
  CardNetworkFilters
} from '@/features/account-management/options';
import { TableView } from '@/design-system/components/table/TableView';
import { TransactionStatus, CardStatus, CardType, CardAccountStatus } from '@/features/account-management/enum';
import { Dropdown } from '@/design-system/components/dropdown';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/design-system/components/tooltip/Tooltip';
import { LastUpdated } from '@/design-system/components/lastUpdated/LastUpdated';
import { TokenPagination } from '@/design-system/components/pagination/TokenPagination';
import { Card, Xmetadata } from '@utx/utx-access-client';
import { phoneNumberProp, useCard, useCardTransaction } from './hooks/useCard.hook';
import { CardAccountType } from '@/features/card-account/enum';
import { DropdownMenu } from '@/design-system/components/dropdown/DropdownMenu';
import { useDropdown } from '@/design-system/components/dropdown/useDropdown.hook';
import { useHasFeature } from '@/design-system/components/hasFeature/useHasFeature.hook';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';
import { useAccountCreditSummary } from '@/features/card-account/hooks/useCardAccounts.hook';
import { RootStackRoutes } from 'types';

export interface CardDetailsProps {
  account: string;
  tab?: number;
  record?: any;
  flag?: string;
  options?: { [key: string]: any };
}

const NoData = () => {
  const { t } = useTranslation('earn');
  return (
    <View sx={styles.noData}>
      <Icon width={60} height={60} name="listZeroState" />
      <TextBody semiBold>{t('earn-invest::glossary.search-no-result')}</TextBody>
    </View>
  );
};

export const CardDetails: React.FC<CardDetailsProps> = (props) => {
  const { account, tab, record, flag, options } = props;
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const showChange = (value: boolean) => {
    setShow(value);
  };

  const [searchId, setSearchId] = useState<string | undefined>(undefined);
  const [cardFilters, setCardFilters] = useState<{
    cardStatus: string | undefined;
    cardSearch: string | undefined;
  }>({
    cardStatus: undefined,
    cardSearch: undefined
  });
  const {
    cards,
    isLoadingCards,
    reloadCards,
    nextTokenCard,
    preTokenCard,
    prevCard,
    nextCard,
    clearCardCurrentPageToken,
    lockCard,
    unlockCard,
    changeCardPin,
    setCardLimit,
    actionResponse,
    actionError,
    cardAccounts,
    isLoadingCardAccounts,
    reloadCardAccounts,
    topUpCardAccount,
    withdrawCardAccount,
    issueCard,
    activateCard,
    cancelCard,
    suspendCard,
    unsuspendCard,
    updateCardPhoneNumberCard,
    postCardaccountsFixedLimit
  } = useCard({
    cardAccountId: account,
    status: cardFilters?.cardStatus,
    cardEmbossedName: cardFilters?.cardSearch,
    cardId: searchId
  });

  const [dateRangeProps, setDateRangeProps] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null
  });
  const handleChangeDateRange = (value: { from: string | null; to: string | null }) => {
    setDateRangeProps(value);
  };
  const [searchTransaction, setSearchTransaction] = useState<string | undefined>(undefined);
  const {
    transactions,
    isLoadingCardTransactions,
    reloadCardTransactions,
    nextToken,
    preToken,
    next,
    prev,
    refreshCardTransactions,
    clearCardTransactionCurrentPageToken
  } = useCardTransaction({
    cardAccountId: account,
    dateRangeFrom: dateRangeProps?.from ? moment(dateRangeProps?.from)?.toISOString() : undefined,
    dateRangeTo: dateRangeProps?.to ? moment(dateRangeProps?.to)?.toISOString() : undefined,
    search: searchTransaction,
    cardId: searchId
  });

  const [showInfo, setShowInfo] = useState<boolean>(true);
  const showChangeInfo = (value: boolean) => {
    setShowInfo(value);
  };

  const getColor = (status: string) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return '#28ACFF';
      case TransactionStatus.SUCCESS:
        return theme?.colors?.$success as string;
      case TransactionStatus.DECLINED:
        return theme?.colors?.$danger as string;
      default:
        return theme?.colors?.$textSecondary as string;
    }
  };

  const getCardColor = (status: string) => {
    switch (status) {
      case CardStatus.ACTIVE:
        return theme?.colors?.$success as string;
      case CardStatus.PENDING_ACTIVATION:
        return '#0EBBBE';
      case CardStatus.LOCKED:
        return '#DABE18';
      case CardStatus.SUSPENDED:
        return theme?.colors?.$tagRedBackground as string;
      case CardStatus.CANCELLED:
        return theme?.colors?.$tagGreyBackground as string;
      default:
        return theme?.colors?.$textSecondary as string;
    }
  };
  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };

  const funcLockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    lockCard(cardId, onSuccess, onError);
  };

  const funcUnlockCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    unlockCard(cardId, onSuccess, onError);
  };

  const funcChangeCardPin = (cardId: string, pin: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    changeCardPin(cardId, pin, onSuccess, onError);
  };

  const funcTopUpCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    topUpCardAccount(accountId, amount, currency, xmetadata, onSuccess, onError);
  };

  const funcWithdrawCardAccount = (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    withdrawCardAccount(accountId, amount, currency, xmetadata, onSuccess, onError);
  };

  const funcSetCardLimit = (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    dailyLimit?: number,
    singleTransactionLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    setCardLimit(cardId, atmLimit, purchaseLimit, dailyLimit, singleTransactionLimit, onSuccess, onError);
  };

  const funcIssueCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    issueCard(data, onSuccess, onError);
  };

  const funcActivateCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    activateCard(cardId, onSuccess, onError);
  };

  const funcCancelCard = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    cancelCard(data, onSuccess, onError);
  };
  const funcSuspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    suspendCard(cardId, onSuccess, onError);
  };

  const funcUnsuspendCard = (cardId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    unsuspendCard(cardId, onSuccess, onError);
  };

  const funcUpdateCardPhoneNumberCard = (
    cardId: string,
    phoneNumber: phoneNumberProp,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    updateCardPhoneNumberCard(cardId, phoneNumber, onSuccess, onError);
  };

  const funcPostCardaccountsFixedLimit = (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    postCardaccountsFixedLimit(limit, onSuccess, onError);
  };

  const [cardAccountsOverview, setCardAccountsOverview] = useState<{
    balance: number;
    limit: number;
    available: number;
    currency: string;
    name: string | undefined;
    accountType: string | undefined;
    walletId?: string;
    status: string | undefined;
    settlementAccountName?: string;
    settlementAccountNumber?: string;
  }>({ balance: 0, limit: 0, available: 0, currency: 'HKD', name: '', accountType: '', walletId: undefined, status: '' });

  const { creditSummary, isLoadingCreditSummary } = useAccountCreditSummary({
    walletId: cardAccountsOverview.walletId,
    currency: cardAccountsOverview.currency,
    accountType: cardAccountsOverview.accountType
  });

  useEffect(() => {
    let balanceAmount = 0;
    let limitAmount = 0;
    let availableAmount = 0;
    let currencyAmount = 'HKD';
    let cardAccountName = undefined;
    let cardAccountType = undefined;
    let walletId = undefined;
    let settlementAccountName = undefined;
    let settlementAccountNumber = undefined;
    if (!isLoadingCardAccounts) {
      if (cardAccounts) {
        cardAccounts.forEach((cardAccount) => {
          balanceAmount += cardAccount?.balance;
          limitAmount += cardAccount?.limit;
          availableAmount += cardAccount?.available;
          currencyAmount = cardAccount?.currency;
          cardAccountName = cardAccount?.name;
          cardAccountType = cardAccount?.accountType;
          walletId = cardAccount?.walletId;
          status = cardAccount?.status;
          settlementAccountName = cardAccount?.settlementAccountName;
          settlementAccountNumber = cardAccount?.settlementAccountNumber;
        });
        setCardAccountsOverview({
          balance: balanceAmount,
          limit: limitAmount,
          available: availableAmount,
          currency: currencyAmount,
          name: cardAccountName,
          accountType: cardAccountType,
          walletId: walletId,
          status: status,
          settlementAccountName: settlementAccountName,
          settlementAccountNumber: settlementAccountNumber
        });
      } else {
        setCardAccountsOverview({
          balance: balanceAmount,
          limit: limitAmount,
          available: availableAmount,
          currency: currencyAmount,
          name: cardAccountName,
          accountType: cardAccountType,
          walletId: walletId,
          status: status,
          settlementAccountName: settlementAccountName,
          settlementAccountNumber: settlementAccountNumber
        });
      }
    }
  }, [isLoadingCardAccounts, cardAccounts]);

  useEffect(() => {
    if (options?.card) {
      setSearchId(options.card.id);
    }
  }, [options]);

  useEffect(() => {
    if (options?.card && cards.length > 0) {
      const card = cards.find((item) => item.id === options.card.id);
      if (card && card.status === CardStatus.PENDING_ACTIVATION) {
        setTimeout(() => {
          navigation.navigate(RootStackRoutes.AccountManagement, {
            screen: AccountManagementStackRoutes.CardActivation,
            params: {
              account: account,
              tab: tab,
              record: card,
              func: funcActivateCard,
              actionResponse: actionResponse,
              actionError: actionError,
              refresh: reloadCards
            }
          });
        }, 500);
      }
    }
  }, [cards]);

  const [selections, setSelections] = useState<{ label: string; value: string }[]>([]);
  const [selectedId, setCardId] = useState<string>('');
  const [selected, setCard] = useState<Card>();
  const onValueChange = (value: string | string[]) => {
    if (!selectedId || !selected || !value) return;
    if (value == 'cancel') {
      navigation.navigate(RootStackRoutes.AccountManagement, {
        screen: AccountManagementStackRoutes.CancelCard,
        params: {
          account: account,
          tab: tab,
          record: selected,
          func: funcCancelCard,
          actionResponse: actionResponse,
          actionError: actionError,
          refresh: reloadCards
        }
      });
    } else {
      navigation.navigate(RootStackRoutes.AccountManagement, {
        screen: AccountManagementStackRoutes.SuspendCard,
        params: {
          account: account,
          tab: tab,
          record: selected,
          func: value == 'suspend' ? funcSuspendCard : funcUnsuspendCard,
          actionResponse: actionResponse,
          actionError: actionError,
          refresh: reloadCards
        }
      });
    }
  };
  const { isOpen, data, onToggle, onSelect } = useDropdown({
    onValueChange,
    options: selections,
    clearFlag: true
  });

  const isEnabled = useHasFeature({ feature: 'ACCESS_CARD_BALANCE_WITHDRAW', variation: 'enabled' })?.enabled;

  return (
    <View sx={styles.contentContainer}>
      <View sx={styles.summaryCard}>
        <PageItem sx={styles.card}>
          <View sx={styles.summaryContent}>
            <View sx={{ width: layout.screenSize === ScreenSize.Tablet ? '27%' : 400 }}>
              <Row sx={{ alignItems: 'center', gap: '$1' }}>
                <TextBody semiBold secondary>
                  {t('card-account-management::account-page.text--card-account-balance')}
                </TextBody>
                <UniIconButton icon={show ? 'hide' : 'show'} onPress={(e) => showChange(!show)} />
              </Row>
              <Row sx={{ mt: '$1' }}>
                <H3
                  bold
                  sx={{
                    color: (cardAccountsOverview?.balance &&
                    cardAccountsOverview?.balance > 0 &&
                    cardAccountsOverview.accountType == CardAccountType.PREPAID
                      ? theme?.colors?.$tagRedBackground
                      : theme?.colors?.$secondary) as string
                  }}>
                  {hideShowBalance(
                    formatAmountByDecimal({
                      value: Math.abs(cardAccountsOverview?.balance),
                      suffix: `${cardAccountsOverview?.currency} ${isNegative(cardAccountsOverview?.balance.toString()) ? '[CR]' : ''}`,
                      decimals: 2
                    })
                  )}
                </H3>
              </Row>
            </View>
            <View sx={{ ml: '$7', flexDirection: 'row', alignItems: 'center', gap: '$7', flex: 1 }}>
              {cardAccountsOverview.accountType && cardAccountsOverview.accountType != CardAccountType.PREPAID && (
                <Row
                  sx={{
                    pt: '$2',
                    pb: '$2',
                    alignItems: 'center',
                    borderRadius: 5
                  }}>
                  <Icon name="limit" width={30} height={24} />
                  <View sx={{ ml: '$4' }}>
                    <TextBody semiBold secondary sx={{ mb: '$1' }}>
                      {t('card-account-management::card-details.text--card-account-limit')}
                    </TextBody>
                    <H6 bold>
                      {hideShowBalance(
                        formatAmountByDecimal({ value: cardAccountsOverview?.limit, suffix: cardAccountsOverview?.currency, decimals: 2 })
                      )}
                    </H6>
                  </View>
                </Row>
              )}
              {cardAccountsOverview.accountType && cardAccountsOverview.accountType != CardAccountType.PREPAID && (
                <Row
                  sx={{
                    pt: '$2',
                    pb: '$2',
                    alignItems: 'center',
                    borderRadius: 5
                  }}>
                  <Icon name="limit" width={30} height={24} />
                  <View sx={{ ml: '$4' }}>
                    <TextBody semiBold secondary sx={{ mb: '$1' }}>
                      {t('card-account-management::card-details.text--available-limit')}
                    </TextBody>
                    <H6 bold>
                      {hideShowBalance(
                        formatAmountByDecimal({
                          value: cardAccountsOverview?.available,
                          suffix: cardAccountsOverview?.currency,
                          decimals: 2
                        })
                      )}
                    </H6>
                  </View>
                </Row>
              )}
              {cardAccountsOverview.accountType && cardAccountsOverview.accountType == CardAccountType.FIXED_LIMIT && (
                <Row
                  sx={{
                    pt: '$2',
                    pb: '$2',
                    alignItems: 'center',
                    borderRadius: 5
                  }}>
                  <Icon name="cardAccount" width={30} height={24} />
                  <View sx={{ ml: '$4' }}>
                    <TextBody semiBold secondary sx={{ mb: '$1' }}>
                      {t('card-account-management::card-details.text--settlement-account')}
                    </TextBody>
                    <H6 bold sx={{ display: 'flex', flexDirection: 'column', gap: '$1' }}>
                      <TextBody>
                        <label>{t('card-account-management::card-details.text--settlement-account-name')}</label>
                        <TextBody semiBold>{cardAccountsOverview?.settlementAccountName}</TextBody>
                      </TextBody>
                      <TextBody>
                        <label>{t('card-account-management::card-details.text--settlement-account-number')}</label>
                        <TextBody semiBold>{cardAccountsOverview?.settlementAccountNumber}</TextBody>
                      </TextBody>
                    </H6>
                  </View>
                </Row>
              )}
              {flag == '1' &&
                !isLoadingCardAccounts &&
                cardAccountsOverview.accountType &&
                cardAccountsOverview.accountType == CardAccountType.PREPAID && (
                  <Row sx={{ justifyContent: 'end', flexDirection: 'row', gap: '$3' }}>
                    <UniButton
                      sx={{ width: 200, height: 44 }}
                      labelSx={{ color: '$black' }}
                      label={t('card-account-management::card-details.button--top-up')}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.TopUp,
                          params: {
                            record: { currency: cardAccountsOverview?.currency, name: cardAccountsOverview?.name },
                            func: funcTopUpCardAccount,
                            refresh: () => {
                              reloadCardAccounts();
                              refreshCardTransactions();
                            }
                          }
                        });
                      }}
                    />
                    {isEnabled && (
                      <UniButton
                        sx={{ width: 200, height: 44 }}
                        labelSx={{ color: '$black' }}
                        label={t('card-account-management::card-details.button--withdraw')}
                        onPress={() => {
                          navigation.navigate(RootStackRoutes.AccountManagement, {
                            screen: AccountManagementStackRoutes.Withdraw,
                            params: {
                              record: {
                                currency: cardAccountsOverview?.currency,
                                name: cardAccountsOverview?.name,
                                available: cardAccountsOverview.available
                              },
                              func: funcWithdrawCardAccount,
                              refresh: () => {
                                reloadCardAccounts();
                                refreshCardTransactions();
                              }
                            }
                          });
                        }}
                      />
                    )}
                  </Row>
                )}
              {flag == '1' &&
                !isLoadingCardAccounts &&
                !isLoadingCreditSummary &&
                cardAccountsOverview.accountType &&
                cardAccountsOverview.accountType == CardAccountType.FIXED_LIMIT && (
                  <Row sx={{ justifyContent: 'end', flexDirection: 'row', gap: '$3' }}>
                    <UniButton
                      sx={{ width: 200, height: 44 }}
                      labelSx={{ color: '$black' }}
                      label={t('card-account-management::card-details.button--fixed-limit')}
                      onPress={() => {
                        navigation.navigate(RootStackRoutes.AccountManagement, {
                          screen: AccountManagementStackRoutes.FixedLimit,
                          params: {
                            record: { currency: cardAccountsOverview?.currency, name: cardAccountsOverview?.name, creditSummary: creditSummary },
                            func: funcPostCardaccountsFixedLimit,
                            refresh: () => {
                              reloadCardAccounts();
                            }
                          }
                        });
                      }}
                    />
                  </Row>
                )}
            </View>
          </View>
        </PageItem>
      </View>
      <View sx={styles.overviewCard}>
        <PageItem sx={styles.card}>
          {/* Card Overview */}
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <H5 bold>{t('card-account-management::card-details.text--card-overview')}</H5>
            {cardAccountsOverview.status != CardAccountStatus.FROZEN && (
              <UniButton
                sx={{ width: 150, height: 44 }}
                labelSx={{ color: '$black' }}
                label={t('card-account-management::issue-card.instruction-form.title--issue-card')}
                onPress={() => {
                  navigation.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.IssueCard,
                    params: {
                      account: account,
                      tab: tab,
                      record: record,
                      func: funcIssueCard,
                      refresh: reloadCards
                    }
                    });
                  }}
              />
            )}
          </View>
          <View sx={{ ...styles.overviewTransaction, zIndex: 1000 }}>
            <View sx={styles.transactionHeader}>
              <Row sx={{ flexDirection: 'row', gap: '$3', height: 36, alignItems: 'center' }}>
                <Dropdown
                  onValueChange={(value) => {
                    clearCardCurrentPageToken();
                    setCardFilters({ ...cardFilters, cardStatus: value == 'ALL' ? undefined : value });
                  }}
                  options={CardStatusSelectFilters(t)}
                  value={cardFilters.cardStatus || 'ALL'}
                  prefix={t('card-account-management::account-page.text--status')}
                  sx={{ width: '$70' }}
                />
                <TextCaption
                  sx={cardFilters.cardStatus != undefined || cardFilters.cardSearch != undefined ? {} : { color: '#CCCCCC' }}
                  onPress={() => setCardFilters({ cardStatus: undefined, cardSearch: undefined })}>
                  {t('card-account-management::general.clear-all-filters')}
                </TextCaption>
              </Row>
              <View>
                <UniInput
                  sx={{ width: 300 }}
                  value={cardFilters.cardSearch || ''}
                  size="default"
                  prefixIcon="search"
                  placeholder={t('card-account-management::glossary.search-by-card-name')}
                  onValueChange={(value) => {
                    clearCardCurrentPageToken();
                    setCardFilters({ ...cardFilters, cardSearch: value || undefined });
                  }}
                />
              </View>
            </View>
            <View sx={styles.transactionContent}>
              <TableView<Card>
                data={cards}
                rowVerticalAlign="center"
                loading={isLoadingCards}
                columns={[
                  {
                    title: t('card-account-management::account-page.text--card'),
                    index: 'product',
                    width: 100,
                    render: (item) => (
                      <Image
                        sx={{ width: 67, height: 42 }}
                        source={item?.product?.imageUrl || require('assets/images/worldcard_blank.png')}></Image>
                    )
                  },
                  {
                    title: t('card-account-management::glossary.card-name-no'),
                    index: 'last4',
                    render: (item) => (
                      <View sx={{ flexDirection: 'column', gap: '$1', alignItems: 'flex-start' }}>
                        <TextBody semiBold sx={{ color: theme?.colors?.$textPrimary as string }}>
                          {item.embossedName}
                        </TextBody>
                        <TextCaption sx={{ color: theme?.colors?.$textSecondary as string }}>
                          {CardNetworkFilters(t).find((rec) => rec.value === item?.network)?.label} {`**** ${item.last4}`}
                        </TextCaption>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::card-details.text--todays-spending'),
                    index: 'cardSpendingToday',
                    sx: { alignItems: 'flex-end' },
                    render: (item) => (
                      <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string }}>
                        {hideShowBalance(
                          formatAmountByDecimal({
                            value: item?.cardSpendingToday || 0,
                            suffix: cardAccountsOverview?.currency,
                            decimals: 2
                          })
                        )}
                      </TextBody>
                    )
                  },
                  {
                    title: t('card-account-management::set-card-limit.text--daily-limit'),
                    index: 'dailyPurchaseLimit',
                    sx: { alignItems: 'flex-end' },
                    render: (item) => (
                      <View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2', justifyContent: 'flex-end' }}>
                          <TextBody sx={{ color: theme?.colors?.$textPrimary as string }}>
                            {item.dailyPurchaseLimit === null
                              ? '-'
                              : hideShowBalance(
                                  formatAmountByDecimal({
                                    value: item?.dailyPurchaseLimit || 0,
                                    suffix: cardAccountsOverview?.currency,
                                    decimals: 2
                                  })
                                )}
                          </TextBody>
                          <Tooltip
                            content={
                              <TextBody sx={styles.tooltipText}>
                                {t('card-account-management::card-details.text--daily-purchase-limit')}
                              </TextBody>
                            }
                            position="middle"
                            contentTextMinWidth={154}
                            showUnderline={false}>
                            <Icon name="purchaseLimit" width={20} height={20} />
                          </Tooltip>
                        </View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2', justifyContent: 'flex-end' }}>
                          <TextBody sx={{ color: theme?.colors?.$textPrimary as string }}>
                            {item.dailyAtmLimit === null
                              ? '-'
                              : hideShowBalance(
                                  formatAmountByDecimal({
                                    value: item?.dailyAtmLimit || 0,
                                    suffix: cardAccountsOverview?.currency,
                                    decimals: 2
                                  })
                                )}
                          </TextBody>
                          <Tooltip
                            content={
                              <TextBody sx={styles.tooltipText}>
                                {t('card-account-management::card-details.text--daily-atm-limit')}
                              </TextBody>
                            }
                            position="middle"
                            contentTextMinWidth={198}
                            showUnderline={false}>
                            <Icon name="atmLimit" width={20} height={20} />
                          </Tooltip>
                        </View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2', justifyContent: 'flex-end' }}>
                          <TextBody sx={{ color: theme?.colors?.$textPrimary as string }}>
                            {item.dailyLimit === null
                              ? '-'
                              : hideShowBalance(
                                  formatAmountByDecimal({
                                    value: item?.dailyLimit || 0,
                                    suffix: cardAccountsOverview?.currency,
                                    decimals: 2
                                  })
                                )}
                          </TextBody>
                          <Tooltip
                            content={
                              <TextBody sx={styles.tooltipText}>{t('card-account-management::card-details.text--daily-limit')}</TextBody>
                            }
                            position="middle"
                            contentTextMinWidth={198}
                            showUnderline={false}>
                            <Icon name="atmLimit" width={20} height={20} />
                          </Tooltip>
                        </View>
                        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$2', justifyContent: 'flex-end' }}>
                          <TextBody sx={{ color: theme?.colors?.$textPrimary as string }}>
                            {item.singleTransactionLimit === null
                              ? '-'
                              : hideShowBalance(
                                  formatAmountByDecimal({
                                    value: item?.singleTransactionLimit || 0,
                                    suffix: cardAccountsOverview?.currency,
                                    decimals: 2
                                  })
                                )}
                          </TextBody>
                          <Tooltip
                            content={
                              <TextBody sx={styles.tooltipText}>
                                {t('card-account-management::card-details.text--single-transaction-limit')}
                              </TextBody>
                            }
                            position="middle"
                            contentTextMinWidth={198}
                            showUnderline={false}>
                            <Icon name="atmLimit" width={20} height={20} />
                          </Tooltip>
                        </View>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::glossary.card-status'),
                    index: 'status',
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Icon name="dotsymbol" color={getCardColor(item?.status)} />
                        <TextCaption sx={{ color: getCardColor(item?.status) }}>
                          {CardStatusFilters(t).find((el) => el.value === item?.status)?.label}
                        </TextCaption>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::glossary.card-actions'),
                    index: '',
                    width: 270,
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', zIndex: 1 }}>
                        {(item.status != CardStatus.CANCELLED && cardAccountsOverview.status != CardAccountStatus.FROZEN) && (
                          <>
                            {item.status != CardStatus.PENDING_ACTIVATION ? (
                              <>
                                <View sx={{ width: '$10', height: '$10', justifyContent: 'center' }}>
                                  <Tooltip
                                    content={
                                      <TextBody sx={styles.tooltipText}>
                                        {item.status == CardStatus.LOCKED
                                          ? t('card-account-management::glossary.unlock-card')
                                          : t('card-account-management::card-details.text--lock-card')}
                                      </TextBody>
                                    }
                                    position="middle"
                                    contentTextMinWidth={89}
                                    showUnderline={false}>
                                    <UniIconButton
                                      icon={item.status == CardStatus.LOCKED ? 'unlockUTGLWeb' : 'lockUTGLWeb'}
                                      onPress={() => {
                                        navigation.navigate(RootStackRoutes.AccountManagement, {
                                          screen: AccountManagementStackRoutes.LockCard,
                                          params: {
                                            account: account,
                                            tab: tab,
                                            record: item,
                                            func: item.status == CardStatus.ACTIVE ? funcLockCard : funcUnlockCard,
                                            actionResponse: actionResponse,
                                            actionError: actionError,
                                            refresh: reloadCards
                                          }
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                </View>
                                <View sx={{ width: '$10', height: '$10', justifyContent: 'center' }}>
                                  <Tooltip
                                    content={
                                      <TextBody sx={styles.tooltipText}>
                                        {t('card-account-management::card-details.text--set-card-limit')}
                                      </TextBody>
                                    }
                                    position="middle"
                                    contentTextMinWidth={114}
                                    showUnderline={false}>
                                    <UniIconButton
                                      icon="settingUTGLWeb"
                                      onPress={() => {
                                        navigation.navigate(RootStackRoutes.AccountManagement, {
                                          screen: AccountManagementStackRoutes.SetCardLimit,
                                          params: {
                                            account: account,
                                            tab: tab,
                                            record: { ...item, limit: cardAccountsOverview.limit, currency: cardAccountsOverview.currency },
                                            func: funcSetCardLimit,
                                            refresh: reloadCards
                                          }
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                </View>
                                <View sx={{ width: '$10', height: '$10', justifyContent: 'center' }}>
                                  <Tooltip
                                    content={
                                      <TextBody sx={styles.tooltipText}>
                                        {t('card-account-management::card-details.text--change-atm-pin')}
                                      </TextBody>
                                    }
                                    position="middle"
                                    contentTextMinWidth={131}
                                    showUnderline={false}>
                                    <UniIconButton
                                      icon="changeATMPIN"
                                      onPress={() => {
                                        navigation.navigate(RootStackRoutes.AccountManagement, {
                                          screen: AccountManagementStackRoutes.ChangeATMPIN,
                                          params: { account: account, tab: tab, record: item, func: funcChangeCardPin }
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                </View>
                                <View sx={{ width: '$10', height: '$10', justifyContent: 'center' }}>
                                  <Tooltip
                                    content={<TextBody sx={styles.tooltipText}>{t('card::glossary.text--view-card-info')}</TextBody>}
                                    position="middle"
                                    contentTextMinWidth={119}
                                    showUnderline={false}>
                                    <UniIconButton
                                      icon="cardWeb"
                                      onPress={(e) => {
                                        navigation.navigate(RootStackRoutes.AccountManagement, {
                                          screen: AccountManagementStackRoutes.CardDetailInfo,
                                          params: { account: account, tab: tab, record: item }
                                        });
                                      }}
                                    />
                                  </Tooltip>
                                </View>
                              </>
                            ) : (
                              <UniButton
                                sx={{
                                  width: 160,
                                  height: 28
                                }}
                                wrapperSx={{ justifyContent: 'center' }}
                                labelSx={{ color: '$balck' }}
                                label={t('credit-card-activate-card::glossary.activate-card')}
                                onPress={() => {
                                  navigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.CardActivation,
                                    params: {
                                      account: account,
                                      tab: tab,
                                      record: item,
                                      func: funcActivateCard,
                                      actionResponse: actionResponse,
                                      actionError: actionError,
                                      refresh: reloadCards
                                    }
                                  });
                                }}
                              />
                            )}

                            <View sx={{ width: '$10', height: '$10', justifyContent: 'center' }}>
                              <Tooltip
                                content={
                                  <TextBody sx={styles.tooltipText}>
                                    {t('card-account-management::replace-card.title--update-mobile-number')}
                                  </TextBody>
                                }
                                position="middle"
                                contentTextMinWidth={180}
                                showUnderline={false}>
                                <UniIconButton
                                  icon="updateMobileNumberUTGL"
                                  onPress={(e) => {
                                    navigation.navigate(RootStackRoutes.AccountManagement, {
                                      screen: AccountManagementStackRoutes.UpdateMobileNo,
                                      params: {
                                        tab: tab,
                                        record: item,
                                        func: funcUpdateCardPhoneNumberCard,
                                        refresh: reloadCards
                                      }
                                    });
                                  }}
                                />
                              </Tooltip>
                            </View>
                            {(item.status === CardStatus.ACTIVE || item.status === CardStatus.SUSPENDED) && (
                              <View sx={{ width: '$10', height: '$10', alignItems: 'center', justifyContent: 'center' }}>
                                <Tooltip
                                  content={
                                    <TextBody sx={styles.tooltipText}>
                                      {t('card-account-management::glossary.cancel-suspend-card')}
                                    </TextBody>
                                  }
                                  position="middle"
                                  contentTextMinWidth={170}
                                  showUnderline={false}>
                                  <UniIconButton
                                    icon="cardCancelSuspended"
                                    onPress={() => {
                                      setSelections(
                                        item.status === CardStatus.ACTIVE
                                          ? [
                                              { label: t('credit-card-cancel-card::glossary.cancel-card'), value: 'cancel' },
                                              { label: t('credit-card-suspend-unsuspend-card::glossary.suspend-card'), value: 'suspend' }
                                            ]
                                          : [
                                              {
                                                label: t('credit-card-suspend-unsuspend-card::glossary.unsuspend-card'),
                                                value: 'unsuspend'
                                              }
                                            ]
                                      );
                                      setCardId(item.id || '');
                                      setCard(item);
                                      onToggle();
                                    }}
                                  />
                                </Tooltip>
                                {isOpen && selectedId === item.id && (
                                  <DropdownMenu
                                    isOpen={isOpen}
                                    data={data}
                                    dropdownLayout={{
                                      top: 20,
                                      left: -116,
                                      width: 156
                                    }}
                                    dropDownItemSx={(hovered, selected) => ({
                                      backgroundColor: hovered ? '$backgroundDropdownActive' : '$backgroundPopup'
                                    })}
                                    onSelect={onSelect}
                                  />
                                )}
                              </View>
                            )}
                          </>
                        )}
                      </View>
                    )
                  }
                ]}
                onRowUpdated={(item) => {
                  return { zIndex: isOpen && item.id === selectedId ? 1 : 0 };
                }}
                emptyComponent={<NoData />}
                sx={{ overflowX: 'visible' }}
              />
            </View>
            <View sx={{ alignItems: 'flex-end', px: '$4', zIndex: -1 }}>
              <TokenPagination prevPageToken={preTokenCard} nextPageToken={nextTokenCard} onNextPage={nextCard} onPrevPage={prevCard} />
            </View>
          </View>
          {/* <PageItem sx={styles.card}> */}
          <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <H5 bold>{t('card-account-management::account-page.title--transaction-history')}</H5>
            <UniButton
              wrapperSx={{ width: 240 }}
              labelSx={{ color: '$black' }}
              label={t('card-account-management::account-page.button--download-transaction-report')}
              onPress={() =>
                navigationAny.navigate(RootStackRoutes.AccountManagement, {
                  screen: AccountManagementStackRoutes.DownloadTransactionReport,
                  params: {
                    screen: DownloadTransactionReportStackRoutes.DownloadCardAccountTransactionReport,
                    params: { creditLineId: account }
                  }
                })
              }
            />
          </View>
          <View sx={styles.overviewTransaction}>
            <View sx={styles.transactionHeader}>
              <Row sx={{ flexDirection: 'row', gap: '$3', height: 36 }}>
                <UniIconButton
                  sx={{
                    borderWidth: 1,
                    borderColor: theme?.colors?.$borderButton as string,
                    borderRadius: 5,
                    width: 36,
                    justifyContent: 'center'
                  }}
                  icon="refresh"
                  onPress={() => {
                    handleChangeDateRange({ from: null, to: null });
                    setSearchTransaction(undefined);
                    refreshCardTransactions();
                  }}
                />
                <DateRangePicker
                  dateRangeProps={dateRangeProps}
                  onValueChange={(value) => {
                    clearCardTransactionCurrentPageToken();
                    handleChangeDateRange({
                      ...dateRangeProps,
                      from: value.from ? moment(value.from) : null,
                      to: value.to ? moment(value.to) : null
                    });
                  }}
                />
              </Row>
              <View>
                <UniInput
                  value={searchTransaction}
                  sx={{ width: 300 }}
                  size="default"
                  prefixIcon="search"
                  placeholder={t('card-account-management::glossary.search-by-description-card-name')}
                  onValueChange={(value) => {
                    clearCardTransactionCurrentPageToken();
                    setSearchTransaction(value);
                  }}
                />
              </View>
            </View>
            <View sx={styles.transactionContent}>
              <TableView<any>
                data={transactions}
                rowVerticalAlign="center"
                loading={isLoadingCardTransactions}
                columns={[
                  {
                    title: t('card-account-management::card-details.text--transaction-date'),
                    index: 'createdAt',
                    width: 150,
                    render: (item) => <TextBody>{item.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : '-'}</TextBody>
                  },
                  {
                    title: t('card-account-management::card-details.text--post-date'),
                    index: 'postedAt',
                    width: 140,
                    render: (item) => <TextBody>{item.postedAt ? moment(item.postedAt).format('YYYY-MM-DD') : '-'}</TextBody>
                  },
                  {
                    title: t('card-account-management::glossary.card-info'),
                    index: 'cardLast4',
                    render: (item) => (
                      <View sx={{ flexDirection: 'column', gap: '$1', alignItems: 'flex-start' }}>
                        <TextBody semiBold sx={{ color: theme?.colors?.$textPrimary as string }}>
                          {item.cardEmbossedName}
                        </TextBody>
                        <TextCaption sx={{ color: theme?.colors?.$textSecondary as string }}>
                          {item.cardLast4 ? `**** **** **** ${item.cardLast4}` : '-'}
                        </TextCaption>
                      </View>
                    ),
                    width: 150
                  },
                  {
                    title: t('card-account-management::account-page.text--description'),
                    index: 'description',
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <TextCaption>{item.description}</TextCaption>
                        <View sx={{ width: '$10', height: '$10', justifyContent: 'center', zIndex: 1 }}>
                          {!!item.xmetadata && (
                            <Tooltip
                              content={
                                <TextBody sx={styles.tooltipText}>
                                  {t('card-account-management::card-details.text--view-xmetadata')}
                                </TextBody>
                              }
                              position="middle"
                              contentTextMinWidth={119}
                              showUnderline={false}>
                              <UniIconButton
                                icon="info"
                                onPress={(e) => {
                                  navigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.Xmetadata,
                                    params: { xmetadata: item.xmetadata }
                                  });
                                }}
                              />
                            </Tooltip>
                          )}
                        </View>
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::card-details.text--amount'),
                    index: 'amount',
                    align: 'right',
                    render: (item) => (
                      <View sx={{ flexDirection: 'column', gap: '$1', alignItems: 'flex-end' }}>
                        <TextBody
                          semiBold
                          sx={{
                            color: (item?.status == 'pending' ? theme?.colors?.$navbarTextInactive : theme?.colors?.$secondary) as string
                          }}>
                          {hideShowBalance(
                            formatAmountByDecimal({
                              value: item.amount,
                              suffix: `${item.currency} ${item?.entryType == 'CREDIT' ? '[CR]' : ''}`,
                              decimals: 2
                            })
                          )}
                        </TextBody>
                        {item?.authorization && item?.authorization?.currency && item.currency !== item?.authorization?.currency && (
                          <TextCaption secondary>
                            {hideShowBalance(
                              formatAmountByDecimal({
                                value: item?.authorization?.amount,
                                suffix: item?.authorization?.currency,
                                decimals: 2
                              })
                            )}
                          </TextCaption>
                        )}
                      </View>
                    )
                  },
                  {
                    title: t('card-account-management::account-page.text--status'),
                    index: 'status',
                    width: 200,
                    sx: { marginLeft: '$10' },
                    render: (item) => (
                      <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Icon name="dotsymbol" color={getColor(item.status)} />
                        <TextCaption sx={{ color: getColor(item.status) }}>
                          {TransactionStatusFilters(t).find((el) => el.value === item.status)?.label}
                        </TextCaption>
                      </View>
                    )
                  }
                ]}
                emptyComponent={<NoData />}
              />
            </View>
            <View sx={styles.transactionFooter}>
              <Row sx={{ flexDirection: 'row', gap: '$1', alignItems: 'center' }}>
                {/* <Icon name="clock1" width={16} height={16} />
                <TextCaption sx={{ color: '$textPlaceholder' }}>{lastUpdated}</TextCaption> */}
                <LastUpdated dateTime={new Date().toISOString()} />
              </Row>
              <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
            </View>
          </View>
          {/* </PageItem> */}
        </PageItem>
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  summaryCard = 'summaryCard',
  summaryContent = 'summaryContent',
  overviewCard = 'overviewCard',
  overviewHeader = 'overviewHeader',
  overviewDetails = 'overviewDetails',
  overviewTransaction = 'overviewTransaction',
  transactionHeader = 'transactionHeader',
  transactionContent = 'transactionContent',
  transactionFooter = 'transactionFooter',
  cardButton = 'cardButton',
  cardButtonContainer = 'cardButtonContainer',
  cardActionBtn = 'cardActionBtn',
  textGroup = 'textGroup',
  tooltip = 'tooltip',
  tooltipText = 'tooltipText',
  tooltipArrow = 'tooltipArrow',
  noData = 'noData'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "summary summary"
      "overview overview"
    `,
    gap: '$5'
  },
  card: {
    flexDirection: 'column',
    padding: '$7',
    gap: '$7',
    borderRadius: 15
  },
  summaryCard: { gridArea: 'summary' },
  summaryContent: { flexDirection: 'row', alignItems: 'center' },
  overviewCard: { gridArea: 'overview' },
  overviewHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  overviewDetails: { flexDirection: 'row' },
  overviewTransaction: { flexDirection: 'column', gap: '$5' },
  transactionHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 },
  transactionContent: {},
  transactionFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  cardButton: {
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 100,
    backgroundColor: '$filterSelected'
  },
  cardButtonContainer: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: '$1',
    marginLeft: -25
  },
  cardActionBtn: {
    width: 130,
    alignItems: 'center',
    gap: '$1'
  },
  textGroup: {
    position: 'absolute',
    top: 0,
    left: '$2',
    height: 242,
    paddingX: '$4',
    paddingTop: '$11',
    paddingBottom: '$5',
    gap: '$3',
    justifyContent: 'flex-end'
  },
  tooltip: {
    position: 'absolute',
    left: -100,
    bottom: '$7',
    padding: '$3',
    borderRadius: 5,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 1,
    alignItems: 'center'
  },
  tooltipText: {
    color: '$textTooltip',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -7,
    width: 0,
    height: 0,
    borderTopWidth: 5,
    borderTopColor: 'transparent',
    borderBottomWidth: 5,
    borderBottomColor: 'transparent',
    borderLeftWidth: 5,
    borderLeftColor: 'rgba(0, 0, 0, 0.85)',
    rotate: '90deg'
  },
  noData: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '$3',
    minHeight: 250
  }
};
